import { Eye, EyeOff, Focus, Lock, MoreHorizontal, Palette, Plus, Trash2, Unlock } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/components/ui/utils'
import { isStageBody, useEditorStore } from '@/stores/editorStore'

import { openChangeTo, TARGET_HINT, TARGET_ICON, targetLabel, useChangeTargets } from './changeTo'
import { KindIcon, useKindUi } from './KindPicker'
import { DOMAIN_WORDS, domainOfFeature, kindColour, kindLabel, prettyId, STAGE_DOMAINS, useVocab, type StageDomain } from './vocab'

/**
 * The accessible mirror of the canvas (§3.2), in four sections — Shapes, Spots, Ways in and out,
 * Paths (plus walk-behind layers when there are any).
 *
 * Everything the canvas can do to a feature — select it, hide it, solo it, lock it, delete it,
 * change its kind or turn it into another kind of thing ("Change to…") — is reachable here with a
 * keyboard and announced by a screen reader. Each section has an "Add" button that asks which kind
 * first, and each row's colour square changes what that row is.
 *
 * A row is a fixed set of slots that always fits its panel, whatever the width: colour square ·
 * name (the only part that gives way, truncated with the full name in its tooltip) · how sure ·
 * show/hide · "…". Everything else lives in the "…" menu (also on right-click, Shift+F10 and the
 * menu key), so no button can be pushed past the edge. The list scrolls inside its panel; the page
 * never does, sideways or down.
 */

export interface LayerListProps {
  className?: string
}

interface Row {
  id: string
  domain: StageDomain | 'layer'
  kind: string
  label: string
  icon?: string
  locked: boolean
  confidence: number
  source: string
}

const SECTION_TITLE: Record<StageDomain | 'layer', string> = {
  region_kind: 'Shapes',
  anchor_kind: 'Spots',
  portal_kind: 'Ways in and out',
  path_kind: 'Paths',
  layer: 'Walk-behind layers',
}

/** An id safe inside an attribute selector (jsdom has no `CSS.escape`). */
const cssId = (id: string): string => (typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id.replace(/["\\]/g, (c) => `\\${c}`))

const EDGE_WORD: Record<string, string> = { left: 'left edge', right: 'right edge', top: 'top edge', bottom: 'bottom edge' }

export function LayerList({ className }: LayerListProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const selection = useEditorStore((s) => s.selection)
  const visibility = useEditorStore((s) => s.visibility)
  const solo = useEditorStore((s) => s.solo)
  const select = useEditorStore((s) => s.select)
  const toggleVisible = useEditorStore((s) => s.toggleVisible)
  const openPicker = useKindUi((s) => s.openPicker)
  const listRef = useRef<HTMLUListElement | null>(null)
  /** The row whose "…" menu is open (a click on "…", a right-click, Shift+F10 or the menu key). */
  const [menuFor, setMenuFor] = useState<string | null>(null)

  const sections = useMemo(() => {
    const out: { domain: StageDomain | 'layer'; rows: Row[] }[] = STAGE_DOMAINS.map((d) => ({ domain: d, rows: [] as Row[] }))
    if (!isStageBody(doc)) return out
    const push = (domain: StageDomain, f: { id: string; kind: string; label?: string; provenance: { locked?: boolean; confidence: number; source: string } }, suffix = '') => {
      const def = vocab.get(f.kind, domain)
      out[STAGE_DOMAINS.indexOf(domain)]!.rows.push({
        id: f.id,
        domain,
        kind: f.kind,
        label: (f.label ?? kindLabel(vocab, f.kind, domain)) + suffix,
        icon: def?.icon,
        locked: f.provenance.locked === true,
        confidence: f.provenance.confidence,
        source: f.provenance.source,
      })
    }
    for (const r of doc.regions) push('region_kind', r)
    for (const a of doc.anchors) push('anchor_kind', a)
    for (const p of doc.portals) {
      const spot = p.anchor_id ? doc.anchors.find((a) => a.id === p.anchor_id) : undefined
      const where = p.edge ? ` (${EDGE_WORD[p.edge] ?? p.edge})` : spot ? ` (on ${(spot.label ?? kindLabel(vocab, spot.kind, 'anchor_kind')).toLowerCase()})` : ''
      push('portal_kind', p, where)
    }
    for (const w of doc.paths) push('path_kind', w)
    if (doc.layers.length) {
      out.push({
        domain: 'layer',
        rows: doc.layers.map((l) => ({
          id: l.id,
          domain: 'layer' as const,
          kind: 'walk_behind',
          label: `${l.z === 'front' ? 'In front' : l.z === 'back' ? 'Behind' : 'Middle'} · ${prettyId(l.id)}`,
          locked: l.provenance.locked === true,
          confidence: l.provenance.confidence,
          source: l.provenance.source,
        })),
      })
    }
    return out
  }, [doc, vocab])

  const rows = useMemo(() => sections.flatMap((s) => s.rows), [sections])
  // One Tab stop into the list (roving tabindex): the selected row, else the first one.
  const tabStop = selection.ids.find((id) => rows.some((r) => r.id === id)) ?? rows[0]?.id

  // Something picked on the picture shows up in the list (the list scrolls, never the page).
  const firstSelected = selection.ids[0]
  useEffect(() => {
    if (!firstSelected) return
    listRef.current?.querySelector<HTMLElement>(`[data-row="${cssId(firstSelected)}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [firstSelected])

  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    // Keys from an open row menu reach here through React's tree (the menu is in a portal): theirs.
    if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) return
    e.preventDefault()
    const index = rows.findIndex((r) => r.id === selection.ids[0])
    const next = e.key === 'ArrowDown' ? Math.min(rows.length - 1, index + 1) : Math.max(0, index - 1)
    const row = rows[next]
    if (row) {
      select([row.id])
      listRef.current?.querySelector<HTMLElement>(`[data-row="${cssId(row.id)}"]`)?.focus()
    }
  }

  return (
    <div className={cn('h-full overflow-y-auto overflow-x-hidden overscroll-contain', className)} data-testid="layer-list">
      {/*
        A tree (sections → rows), not a listbox: the editor treats focus inside a listbox as "a
        drop-down is open" and stays out of its way, which would make Ctrl+Z / Ctrl+S / Esc dead
        after clicking a row here.
      */}
      <ul ref={listRef} className="min-w-0 p-2" role="tree" aria-label="Everything marked on this picture" aria-multiselectable onKeyDown={onKeyDown}>
        {sections.map(({ domain, rows: items }) => (
          <li key={domain} role="none" data-section={domain} className="min-w-0">
            <div className="flex min-w-0 items-center gap-2 px-2 pb-1 pt-3">
              <h3 className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-ink-3" title={SECTION_TITLE[domain]}>
                {SECTION_TITLE[domain]} <span className="font-normal">({items.length})</span>
              </h3>
              {domain !== 'layer' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs"
                  aria-label={`Add a ${DOMAIN_WORDS[domain].one}`}
                  onClick={() => openPicker(domain, { kind: 'add' })}
                >
                  <Plus aria-hidden /> Add
                </Button>
              ) : null}
            </div>
            {items.length === 0 ? (
              <p className="px-2 pb-1 text-xs text-ink-3">{domain === 'layer' ? '' : `No ${DOMAIN_WORDS[domain].many} yet.`}</p>
            ) : (
              <ul role="group" aria-label={SECTION_TITLE[domain]} className="min-w-0">
                {items.map((row) => {
                  const selected = selection.ids.includes(row.id)
                  const shown = solo ? solo === row.id : (visibility[row.id] ?? true)
                  const colour = row.domain === 'layer' ? kindColour(vocab, 'walk_behind', 'region_kind') : kindColour(vocab, row.kind, row.domain)
                  const menuOpen = menuFor === row.id
                  // The actions keep their room at all times (no jump on hover); they show when the
                  // row is picked, hovered, focused, hidden or its menu is open — and always on touch.
                  const showActions = selected || !shown || menuOpen
                  // One Tab stop per list: only the focused (selected) row's own buttons are in the Tab
                  // order, so Tab leaves the list instead of walking every row's buttons.
                  const tabbable = row.id === tabStop
                  const sure = row.source !== 'human' ? `, ${Math.round(row.confidence * 100)}% sure` : ''
                  return (
                    <li key={row.id} role="none" className="min-w-0">
                      <div
                        data-row={row.id}
                        role="treeitem"
                        tabIndex={row.id === tabStop ? 0 : -1}
                        aria-selected={selected}
                        aria-haspopup="menu"
                        // Its own short name; otherwise it would read out every button's label too.
                        aria-label={`${row.label}${sure}${shown ? '' : ', hidden'}${row.locked ? ', locked' : ''}`}
                        onClick={(e) => {
                          // A click inside the row's menu (a portal) bubbles here through React: not a row click.
                          if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) return
                          select([row.id], e.shiftKey)
                        }}
                        onContextMenu={(e) => {
                          if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) return
                          e.preventDefault()
                          if (!selected) select([row.id])
                          setMenuFor(row.id)
                        }}
                        onKeyDown={(e) => {
                          // Only the row's own keys: its buttons (and its menu) handle theirs.
                          if (e.target !== e.currentTarget) return
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            select([row.id])
                          } else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                            e.preventDefault()
                            if (!selected) select([row.id])
                            setMenuFor(row.id)
                          }
                        }}
                        className={cn(
                          'group flex min-w-0 cursor-pointer items-center gap-2 rounded-md py-1 pl-2 pr-1 text-sm',
                          selected ? 'bg-accent-soft text-ink' : 'hover:bg-paper-2',
                          !shown && 'opacity-50',
                        )}
                      >
                        {row.domain !== 'layer' ? (
                          <button
                            type="button"
                            className="inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-line hover:ring-2 hover:ring-accent focus-visible:focus-ring"
                            style={{ background: colour }}
                            tabIndex={tabbable ? 0 : -1}
                            aria-label={`Change what “${row.label}” is`}
                            title="Change what this is"
                            onClick={(e) => {
                              e.stopPropagation()
                              select([row.id])
                              openPicker(row.domain as StageDomain, { kind: 'set', ids: [row.id] })
                            }}
                          >
                            <KindIcon name={row.icon} className="size-3.5 text-ink" />
                          </button>
                        ) : (
                          <span className="size-6 shrink-0 rounded-md" style={{ background: colour }} aria-hidden />
                        )}
                        <span className="min-w-0 flex-1 truncate" title={row.label} data-testid="row-label">
                          {row.label}
                        </span>
                        {row.source !== 'human' ? (
                          <Badge
                            tone={row.confidence >= 0.75 ? 'ok' : row.confidence >= 0.5 ? 'warn' : 'neutral'}
                            className="shrink-0 px-1.5 tabular-nums"
                            title={`${Math.round(row.confidence * 100)}% sure`}
                          >
                            {Math.round(row.confidence * 100)}%
                          </Badge>
                        ) : null}
                        <span
                          className={cn(
                            'flex shrink-0 items-center',
                            showActions ? 'visible' : 'invisible group-focus-within:visible group-hover:visible pointer-coarse:visible',
                          )}
                          data-testid="row-actions"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 [&_svg]:size-4"
                            tabIndex={tabbable ? 0 : -1}
                            aria-label={shown ? `Hide ${row.label}` : `Show ${row.label}`}
                            title={shown ? 'Hide' : 'Show'}
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleVisible(row.id)
                            }}
                          >
                            {shown ? <Eye /> : <EyeOff />}
                          </Button>
                          <RowMenu row={row} open={menuOpen} onOpenChange={(o) => setMenuFor(o ? row.id : null)} shown={shown} tabbable={tabbable} />
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* A row's "…" menu                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Everything a row can do besides show/hide: change its kind, change it into another kind of thing,
 * solo, lock, delete. Acting on a row that is part of a bigger selection acts on the whole
 * selection (the item says how many), like the picture does.
 */
function RowMenu({ row, open, onOpenChange, shown, tabbable }: { row: Row; open: boolean; onOpenChange(open: boolean): void; shown: boolean; tabbable: boolean }) {
  const doc = useEditorStore((s) => s.doc)
  const selection = useEditorStore((s) => s.selection)
  const solo = useEditorStore((s) => s.solo)
  const select = useEditorStore((s) => s.select)
  const toggleVisible = useEditorStore((s) => s.toggleVisible)
  const setSolo = useEditorStore((s) => s.setSolo)
  const setLocked = useEditorStore((s) => s.setLocked)
  const deleteSelection = useEditorStore((s) => s.deleteSelection)
  const openPicker = useKindUi((s) => s.openPicker)

  const ids = selection.ids.length > 1 && selection.ids.includes(row.id) ? selection.ids : [row.id]
  const many = ids.length > 1
  const sameList = row.domain === 'layer' ? [] : ids.filter((id) => domainOfFeature(doc, id) === row.domain)
  const targets = useChangeTargets(ids)
  const words = row.domain === 'layer' ? null : DOMAIN_WORDS[row.domain]

  // An item that opens a dialog hands the focus to it: the closing menu must not pull the focus
  // back to its "…" button (the next key would reopen the menu instead of typing in the search).
  const handedOff = useRef(false)
  const toDialog = (fn: () => void) => {
    handedOff.current = true
    fn()
  }

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 [&_svg]:size-4"
          aria-label={`More for ${row.label}`}
          tabIndex={tabbable ? 0 : -1}
          title="More (or right-click)"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 max-w-[calc(100vw-1rem)]"
        data-testid="row-menu"
        onCloseAutoFocus={(e) => {
          if (!handedOff.current) return
          handedOff.current = false
          e.preventDefault()
        }}
      >
        <DropdownMenuLabel className="truncate normal-case tracking-normal" title={row.label}>
          {many ? `${ids.length} selected` : row.label}
        </DropdownMenuLabel>
        {words ? (
          <DropdownMenuItem
            onSelect={() => {
              select(ids)
              toDialog(() => openPicker(row.domain as StageDomain, { kind: 'set', ids: sameList }))
            }}
          >
            <Palette aria-hidden />
            <span className="min-w-0 flex-1 truncate">{sameList.length > 1 ? `Change what these ${sameList.length} ${words.many} are…` : `Another kind of ${words.one}…`}</span>
          </DropdownMenuItem>
        ) : null}
        {targets.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{many ? `Change all ${ids.length} to` : 'Change to'}</DropdownMenuLabel>
            {targets.map((to) => {
              const Icon = TARGET_ICON[to]
              return (
                <DropdownMenuItem
                  key={to}
                  data-change-to={to}
                  title={TARGET_HINT[to]}
                  onSelect={() => {
                    select(ids)
                    toDialog(() => openChangeTo(to as StageDomain, ids))
                  }}
                >
                  <Icon aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{targetLabel(to, many)}</span>
                </DropdownMenuItem>
              )
            })}
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => toggleVisible(row.id)}>
          {shown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
          {shown ? 'Hide' : 'Show'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setSolo(row.id)}>
          <Focus aria-hidden />
          {solo === row.id ? 'Show everything again' : 'Show only this'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setLocked(row.id, !row.locked)}>
          {row.locked ? <Unlock aria-hidden /> : <Lock aria-hidden />}
          <span className="min-w-0 flex-1 truncate">{row.locked ? 'Unlock (a re-run may change it)' : 'Lock (a re-run leaves it alone)'}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onSelect={() => {
            select(ids)
            deleteSelection()
          }}
        >
          <Trash2 aria-hidden />
          {many ? `Delete ${ids.length} things` : 'Delete'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
